import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import caretDownIcon from "../../icons/svg/caret-down.svg";

export interface SelectOption {
  value: string;
  label: string;
  displayValue?: string; // Optional custom display value for the button
}

interface SelectProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export const Select: React.FC<SelectProps> = ({
  id,
  value,
  onChange,
  options,
  placeholder = "Select...",
  disabled = false,
  className = ""
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number; width: number } | null>(null);
  const selectRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const selectedOption = options.find((opt) => opt.value === value);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        selectRef.current &&
        !selectRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setPosition(null);
      }
    };

    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width
      });

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    } else {
      setPosition(null);
    }
  }, [isOpen]);

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
    setPosition(null);
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        id={id}
        className={`ob-input ob-select-button ${className}`}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (!disabled) {
            setIsOpen(!isOpen);
          }
        }}
        disabled={disabled}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <span className={selectedOption ? "" : "ob-select-placeholder"}>
          {selectedOption ? (selectedOption.displayValue ?? selectedOption.label) : placeholder}
        </span>
        <img
          src={caretDownIcon}
          alt=""
          className="ob-select-chevron"
          width={12}
          height={12}
          style={{
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s ease-out"
          }}
          />
      </button>
      {isOpen && position && createPortal(
        <div
          ref={selectRef}
          className="ob-select-dropdown"
          style={{
            position: "fixed",
            top: `${position.top}px`,
            left: `${position.left}px`,
            width: `${position.width}px`,
            zIndex: 10001
          }}
        >
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`ob-select-option ${value === option.value ? "ob-select-option--selected" : ""}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleSelect(option.value);
              }}
            >
              {value === option.value && (
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="ob-select-check"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
              <span>{option.label}</span>
            </button>
          ))}
        </div>,
        document.body
      )}
    </>
  );
};
